import type { NavigateFunction } from "react-router-dom";
import {
  CHAT_RECOMMENDATIONS,
  isRecommendationVisibleForPersona,
  type ChatRecommendationDefinition,
} from "@/config/chatRecommendations";
import { flattenSidebarNavItems } from "@/config/sidebarNav";
import { getSidebarSectionsForPersona, type WorkspacePersona } from "@/config/workspacePersona";
import { navigateToChatEntryForApp } from "@/lib/chatNavigation";

/** Flat list of sidebar nav item ids visible for a persona. */
export function getVisibleNavItemIds(persona: WorkspacePersona): string[] {
  return getSidebarSectionsForPersona(persona).flatMap((section) =>
    flattenSidebarNavItems(section.items).map((item) => item.id),
  );
}

/** Recommendations that cover a given sidebar nav item for this persona. */
export function getRecommendationsForNavItem(
  navItemId: string,
  persona: WorkspacePersona
): ChatRecommendationDefinition[] {
  return CHAT_RECOMMENDATIONS.filter(
    (rec) =>
      rec.sidebarNavItemId === navItemId && isRecommendationVisibleForPersona(rec, persona)
  );
}

/** Nav item ids with no matching recommendation for this persona (test guard). */
export function getNavItemsMissingRecommendationCoverage(persona: WorkspacePersona): string[] {
  const missing: string[] = [];
  for (const navItemId of getVisibleNavItemIds(persona)) {
    if (getRecommendationsForNavItem(navItemId, persona).length === 0) {
      missing.push(navItemId);
    }
  }
  return missing;
}

export type HubHeroChip = {
  id: string;
  label: string;
  icon: ChatRecommendationDefinition["icon"];
  onClick: (navigate: NavigateFunction, persona: WorkspacePersona) => void;
};

/** Registry-driven hub quick chips (persona-gated, ordered). */
export function getHubHeroChips(persona: WorkspacePersona): HubHeroChip[] {
  return CHAT_RECOMMENDATIONS.filter(
    (rec) => rec.showInHubHero && isRecommendationVisibleForPersona(rec, persona)
  )
    .sort((a, b) => (a.hubChipOrder ?? 99) - (b.hubChipOrder ?? 99))
    .map((rec) => ({
      id: rec.id,
      label: rec.hubChipLabel ?? rec.label.replace(/^(Open the |Try )/i, ""),
      icon: rec.icon,
      onClick: (navigate, chipPersona) => {
        if (rec.kind === "tool" && rec.route) {
          navigate(rec.route);
          return;
        }
        if (rec.kind === "chat" && rec.chatEntry) {
          navigateToChatEntryForApp(navigate, rec.chatEntry, false, chipPersona);
        }
      },
    }));
}

/** Short examples shown under the hub input for discoverability. */
export function getHubCommandHints(persona: WorkspacePersona): string {
  const hints = CHAT_RECOMMENDATIONS.filter(
    (rec) => rec.showInHubHero && isRecommendationVisibleForPersona(rec, persona)
  )
    .sort((a, b) => (a.hubChipOrder ?? 99) - (b.hubChipOrder ?? 99))
    .slice(0, 4)
    .map((rec) => rec.hubHintExample)
    .filter((h): h is string => Boolean(h));

  if (!hints.length) {
    return "Try: reset my nervous system, practice a student conversation…";
  }
  return `Try: ${hints.join(", ")}…`;
}
